/**
 * ViolationCategoryDetail - Detail view for drilling into specific BASIC category
 *
 * Features:
 * - Top violation codes in category
 * - Severity distribution pie chart
 * - Geographic distribution bar chart
 * - Timeline trend line chart
 * - Detailed violations table
 * - Related sub-charts for further drill-down
 */

import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  AlertTriangle,
  AlertCircle,
  TrendingUp,
  MapPin,
  FileText,
  Clock,
  Shield,
} from 'lucide-react';
import { useDrillDown } from '../../contexts/DrillDownContext';
import { Violation } from '../../types/api';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ViolationCodeData {
  code: string;
  count: number;
  name?: string;
}

interface StateViolationData {
  state: string;
  count: number;
  violations?: number;
}

interface ViolationCategoryDetailProps {
  data: {
    category?: string;
    basicCategory?: string;
    violations?: Violation[];
    breakdown?: {
      byCode?: Record<string, Violation[]>;
      bySeverity?: Record<string, Violation[]>;
      byState?: Record<string, Violation[]>;
    };
    totalCount?: number;
    oosCount?: number;
    month?: string;
  };
}

// ============================================================================
// CONSTANTS
// ============================================================================

import { SEVERITY_COLORS, BASIC_COLORS, CHART_COLORS } from '../../constants/colors';

// ============================================================================
// COMPONENT
// ============================================================================

const ViolationCategoryDetail: React.FC<ViolationCategoryDetailProps> = ({ data }) => {
  const { drillDown } = useDrillDown();

  const categoryName = data?.category || data?.basicCategory || 'Unknown Category';
  const violations = data?.violations || [];

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const total = violations.length || data?.totalCount || 0;
    const oos = violations.filter(v => v.outOfServiceIndicator === 'Y').length || data?.oosCount || 0;
    const highSeverity = violations.filter(v => (v.severityWeight || 0) >= 7).length;
    const mediumSeverity = violations.filter(v => (v.severityWeight || 0) >= 4 && (v.severityWeight || 0) < 7).length;
    const lowSeverity = violations.filter(v => (v.severityWeight || 0) < 4).length;

    return {
      total,
      oos,
      oosRate: total > 0 ? ((oos / total) * 100).toFixed(1) : '0',
      highSeverity,
      mediumSeverity,
      lowSeverity,
    };
  }, [violations, data]);

  // Top violation codes
  const topViolationCodes = useMemo(() => {
    if (data?.breakdown?.byCode) {
      return Object.entries(data.breakdown.byCode)
        .map(([code, items]) => ({ code, count: items.length }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
    }

    const codeMap = new Map<string, number>();
    violations.forEach(v => {
      const code = v.violationCode || v.partNo || 'Unknown';
      codeMap.set(code, (codeMap.get(code) || 0) + 1);
    });

    return Array.from(codeMap.entries())
      .map(([code, count]) => ({ code, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [violations, data]);

  // Severity distribution
  const severityDistribution = useMemo(() => {
    return [
      { name: 'High', value: summaryStats.highSeverity, color: SEVERITY_COLORS.high },
      { name: 'Medium', value: summaryStats.mediumSeverity, color: SEVERITY_COLORS.medium },
      { name: 'Low', value: summaryStats.lowSeverity, color: SEVERITY_COLORS.low },
    ].filter(item => item.value > 0);
  }, [summaryStats]);

  // Geographic distribution
  const geographicDistribution = useMemo(() => {
    if (data?.breakdown?.byState) {
      return Object.entries(data.breakdown.byState)
        .map(([state, items]) => ({ state, count: items.length }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
    }

    const stateMap = new Map<string, number>();
    violations.forEach(v => {
      const state = v.reportState || v.state || 'Unknown';
      stateMap.set(state, (stateMap.get(state) || 0) + 1);
    });

    return Array.from(stateMap.entries())
      .map(([state, count]) => ({ state, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [violations, data]);

  // Timeline trend
  const timelineTrend = useMemo(() => {
    const monthMap = new Map<string, number>();

    violations.forEach(v => {
      const dateStr = v.inspDate || v.inspectionDate;
      if (dateStr) {
        const date = new Date(dateStr);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        monthMap.set(key, (monthMap.get(key) || 0) + 1);
      }
    });

    return Array.from(monthMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-12)
      .map(([month, count]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        count,
      }));
  }, [violations]);

  // Handle click on violation code
  const handleCodeClick = (code: string) => {
    const codeViolations = violations.filter(
      v => (v.violationCode || v.partNo) === code
    );
    drillDown('violation-code', code, {
      code,
      violations: codeViolations,
      parentCategory: categoryName,
    });
  };

  // Handle click on state
  const handleStateClick = (state: string) => {
    const stateViolations = violations.filter(
      v => (v.reportState || v.state) === state
    );
    drillDown('state-detail', state, {
      state,
      violations: stateViolations,
      category: categoryName,
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Category Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '1rem',
        backgroundColor: `${BASIC_COLORS[categoryName] || '#23211d'}15`,
        borderLeft: `4px solid ${BASIC_COLORS[categoryName] || '#23211d'}`,
        borderRadius: '8px',
      }}>
        <Shield size={28} style={{ color: BASIC_COLORS[categoryName] || '#23211d' }} />
        <div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0, color: 'var(--color-text)' }}>
            {categoryName}
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', margin: 0 }}>
            BASIC Category Detailed Analysis
          </p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="drill-down-stats-row">
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value">{summaryStats.total.toLocaleString()}</div>
          <div className="drill-down-stat-label">Total Violations</div>
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value" style={{ color: 'var(--color-danger)' }}>
            {summaryStats.oos.toLocaleString()}
          </div>
          <div className="drill-down-stat-label">Out of Service</div>
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value">{summaryStats.oosRate}%</div>
          <div className="drill-down-stat-label">OOS Rate</div>
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value" style={{ color: SEVERITY_COLORS.high }}>
            {summaryStats.highSeverity.toLocaleString()}
          </div>
          <div className="drill-down-stat-label">High Severity</div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="drill-down-detail-grid">
        {/* Top Violation Codes */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <FileText size={18} style={{ color: 'var(--color-primary)' }} />
              Top Violation Codes
            </h4>
            <span className="drill-down-detail-card-badge">
              {topViolationCodes.length} codes
            </span>
          </div>
          {topViolationCodes.length > 0 ? (
            <div className="chart-clickable">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={topViolationCodes} layout="vertical" margin={{ left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd1" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis
                    type="category"
                    dataKey="code"
                    tick={{ fontSize: 10 }}
                    width={55}
                  />
                  <Tooltip
                    contentStyle={{ fontSize: '0.8125rem' }}
                    formatter={(value: number) => [value.toLocaleString(), 'Count']}
                  />
                  <Bar
                    dataKey="count"
                    fill="#23211d"
                    onClick={(data) => handleCodeClick((data as unknown as ViolationCodeData).code)}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No violation code data available
            </div>
          )}
        </div>

        {/* Severity Distribution */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <AlertTriangle size={18} style={{ color: 'var(--color-warning)' }} />
              Severity Distribution
            </h4>
          </div>
          {severityDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={severityDistribution}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={(props: { name?: string; percent?: number }) => `${props.name || ''}: ${((props.percent || 0) * 100).toFixed(0)}%`}
                >
                  {severityDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => [value.toLocaleString(), 'Count']} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No severity data available
            </div>
          )}
        </div>

        {/* Geographic Distribution */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <MapPin size={18} style={{ color: 'var(--color-success)' }} />
              Geographic Distribution
            </h4>
            <span className="drill-down-detail-card-badge">
              Top 10 States
            </span>
          </div>
          {geographicDistribution.length > 0 ? (
            <div className="chart-clickable">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={geographicDistribution} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd1" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="state" tick={{ fontSize: 11 }} width={35} />
                  <Tooltip
                    contentStyle={{ fontSize: '0.8125rem' }}
                    formatter={(value: number) => [value.toLocaleString(), 'Violations']}
                  />
                  <Bar
                    dataKey="count"
                    fill="#5d7148"
                    onClick={(data) => handleStateClick((data as unknown as StateViolationData).state)}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No geographic data available
            </div>
          )}
        </div>

        {/* Timeline Trend */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <TrendingUp size={18} style={{ color: 'var(--color-info)' }} />
              Timeline Trend
            </h4>
            <span className="drill-down-detail-card-badge">
              Last 12 Months
            </span>
          </div>
          {timelineTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={timelineTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd1" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ fontSize: '0.8125rem' }}
                  formatter={(value: number) => [value.toLocaleString(), 'Violations']}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#23211d"
                  strokeWidth={2}
                  dot={{ fill: '#23211d', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No timeline data available
            </div>
          )}
        </div>
      </div>

      {/* Violations Table */}
      {violations.length > 0 && (
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <AlertCircle size={18} style={{ color: 'var(--color-warning)' }} />
              Recent Violations
            </h4>
            <span className="drill-down-detail-card-badge">
              {Math.min(violations.length, 50)} of {violations.length}
            </span>
          </div>
          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border)', position: 'sticky', top: 0, backgroundColor: '#fbf9f4' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Date</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Code</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>State</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Severity</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>OOS</th>
                </tr>
              </thead>
              <tbody>
                {violations.slice(0, 50).map((v, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.75rem' }}>
                      {v.inspDate || v.inspectionDate
                        ? new Date(v.inspDate || v.inspectionDate).toLocaleDateString()
                        : '-'}
                    </td>
                    <td style={{ padding: '0.75rem', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {v.violationCode || v.partNo || '-'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {v.reportState || v.state || '-'}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        backgroundColor: (v.severityWeight || 0) >= 7 ? 'rgba(179, 65, 44, 0.1)' :
                                        (v.severityWeight || 0) >= 4 ? 'rgba(176, 125, 43, 0.1)' :
                                        'rgba(93, 113, 72, 0.1)',
                        color: (v.severityWeight || 0) >= 7 ? SEVERITY_COLORS.high :
                               (v.severityWeight || 0) >= 4 ? SEVERITY_COLORS.medium :
                               SEVERITY_COLORS.low,
                      }}>
                        {v.severityWeight || 0}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      {v.outOfServiceIndicator === 'Y' ? (
                        <span style={{ color: 'var(--color-danger)', fontWeight: 600 }}>Yes</span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViolationCategoryDetail;
